import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  GraduationCap,
  CheckCircle2,
  Circle,
  Clock,
  Trophy,
  BookOpen,
  FileQuestion,
  ChevronRight,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Full-screen progress overlay that shows on the side
export function ProgressPanel({ 
  tourId, 
  isOpen, 
  onClose, 
  modules = [], 
  quizzes = [],
  onStartQuiz,
  onGoToModule
}) {
  const { token } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProgress = useCallback(async () => {
    if (!tourId || !token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/elearning/progress/tour/${tourId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProgress(data);
      }
    } catch (err) {
      console.error('Failed to fetch progress:', err);
    } finally {
      setLoading(false);
    }
  }, [tourId, token]);

  useEffect(() => {
    if (isOpen) {
      fetchProgress();
    }
  }, [isOpen, fetchProgress]);

  const isModuleComplete = (moduleId) => progress?.completed_modules?.includes(moduleId);
  const isQuizComplete = (quizId) => progress?.completed_quizzes?.includes(quizId);

  const formatTime = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-purple-500" />
            Training Progress
          </DialogTitle>
          <DialogDescription>
            Track your progress through this training course
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 flex-1 overflow-auto">
          {/* Overall Progress */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-2xl font-bold text-purple-500">
                {progress?.progress_percentage?.toFixed(0) || 0}%
              </span>
            </div>
            <Progress value={progress?.progress_percentage || 0} className="h-3" />
            
            {progress?.completed_at && (
              <Badge className="bg-green-500 w-full justify-center py-2">
                <Trophy className="h-4 w-4 mr-2" />
                Course Completed!
              </Badge>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="p-3 text-center">
              <Clock className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <div className="text-lg font-bold">{formatTime(progress?.total_time_seconds)}</div>
              <div className="text-xs text-muted-foreground">Time Spent</div>
            </Card>
            <Card className="p-3 text-center">
              <BookOpen className="h-5 w-5 mx-auto mb-1 text-blue-500" />
              <div className="text-lg font-bold">
                {progress?.completed_modules?.length || 0}/{modules.length}
              </div>
              <div className="text-xs text-muted-foreground">Modules</div>
            </Card>
            <Card className="p-3 text-center">
              <FileQuestion className="h-5 w-5 mx-auto mb-1 text-purple-500" />
              <div className="text-lg font-bold">
                {progress?.completed_quizzes?.length || 0}/{quizzes.length}
              </div>
              <div className="text-xs text-muted-foreground">Quizzes</div>
            </Card>
          </div>

          {/* Modules List */}
          {modules.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Training Modules</h4>
              <ScrollArea className="h-[150px]">
                <div className="space-y-1">
                  {modules.map((module, index) => (
                    <button
                      key={module.id}
                      onClick={() => onGoToModule?.(module)}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                        isModuleComplete(module.id)
                          ? 'bg-green-500/10 text-green-600'
                          : 'hover:bg-muted'
                      }`}
                    >
                      {isModuleComplete(module.id) ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <span className="text-sm truncate flex-1">
                        {index + 1}. {module.title}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Quizzes List */}
          {quizzes.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Quizzes</h4>
              <div className="space-y-1">
                {quizzes.map((quiz) => (
                  <button
                    key={quiz.id}
                    onClick={() => onStartQuiz?.(quiz)}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                      isQuizComplete(quiz.id)
                        ? 'bg-green-500/10 text-green-600'
                        : 'hover:bg-muted'
                    }`}
                  >
                    {isQuizComplete(quiz.id) ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <FileQuestion className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    )}
                    <span className="text-sm truncate flex-1">{quiz.title}</span>
                    <Badge variant="outline" className="text-xs">
                      {quiz.questions?.length || 0} Q
                    </Badge>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="pt-4 border-t">
          <Button variant="outline" onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Floating progress bar that stays on screen
export function FloatingProgressBar({ tourId, onClick }) {
  const { token } = useAuth();
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    const fetchProgress = async () => {
      if (!tourId || !token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/elearning/progress/tour/${tourId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setProgress(await res.json());
        }
      } catch (err) {
        console.error('Failed to fetch progress:', err);
      }
    };

    fetchProgress();
    const interval = setInterval(fetchProgress, 30000);
    return () => clearInterval(interval);
  }, [tourId, token]);

  if (!progress) return null;

  const percentage = progress.progress_percentage || 0;

  return (
    <button
      onClick={onClick}
      className="fixed bottom-28 left-1/2 transform -translate-x-1/2 z-30 pointer-events-auto"
    >
      <Card className="bg-card/95 backdrop-blur-md border-border/50 shadow-lg px-4 py-2 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-5 w-5 text-purple-500" />
          <div className="w-32">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-bold text-purple-500">{percentage.toFixed(0)}%</span>
            </div>
            <Progress value={percentage} className="h-2" />
          </div>
          {progress.completed_at && (
            <Trophy className="h-5 w-5 text-yellow-500" />
          )}
        </div>
      </Card>
    </button>
  );
}

export default ProgressPanel;
